import React, { useEffect, useRef } from 'react';
import './experimentos/experimentos.css';

const ExperimentosPage: React.FC = () => {
    const parallaxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Animaciones de entrada para elementos
        const animateOnScroll = () => {
            const elements = document.querySelectorAll('.animate-on-scroll');
            elements.forEach((element) => {
                const elementTop = element.getBoundingClientRect().top;
                const elementVisible = 150;

                if (elementTop < window.innerHeight - elementVisible) {
                    element.classList.add('animate-visible');
                }
            });
        };

        // Ejecutar animación inicial con CSS
        setTimeout(() => {
            const heroTitle = document.querySelector('.hero-title');
            const heroSubtitle = document.querySelector('.hero-subtitle');
            
            if (heroTitle) heroTitle.classList.add('animate-fade-in-up');
            if (heroSubtitle) heroSubtitle.classList.add('animate-fade-in-up');
        }, 100);

        // Escuchar scroll para animaciones
        window.addEventListener('scroll', animateOnScroll);
        animateOnScroll(); // Ejecutar una vez al cargar

        return () => {
            window.removeEventListener('scroll', animateOnScroll);
        };
    }, []);

    const experimentos = [
        {
            id: 2,
            titulo: "Experimento 2",
            descripcion: "Experimento científico del Libro 1 - Actividad 2",
            documento: "/documents/libro1/experimentoactividad2.pdf",
            imagen: "/images/landingpage/PepitoLibro.png",
            objetivos: [
                "Desarrollar habilidades de observación científica",
                "Fomentar el pensamiento crítico",
                "Promover el trabajo en equipo"
            ]
        },
        {
            id: 4,
            titulo: "Experimento 4",
            descripcion: "Experimento científico del Libro 1 - Actividad 4",
            documento: "/documents/libro1/experimentoactividad4.pdf",
            imagen: "/images/landingpage/SarahLibro.png",
            objetivos: [
                "Explorar conceptos básicos de física",
                "Mejorar la comprensión de causa y efecto",
                "Desarrollar habilidades de medición"
            ],
            tarjetas: [
                "/images/experimento4/tarjeta 1.png",
                "/images/experimento4/tarjeta 2.png",
                "/images/experimento4/tarjeta 3.png",
                "/images/experimento4/tarjeta 4.png"
            ]
        },
        {
            id: 7,
            titulo: "Experimento 7",
            descripcion: "Experimento científico del Libro 1 - Actividad 7",
            documento: "/documents/libro1/experimentoactividad7.pdf",
            imagen: "/images/landingpage/AlejandroLibro.png",
            objetivos: [
                "Introducir conceptos de química básica",
                "Fomentar la curiosidad científica",
                "Desarrollar habilidades de registro de datos"
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
            {/* Sección Hero */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 opacity-90"></div>
                <div className="absolute inset-0 bg-black opacity-20"></div>
                
                {/* Elementos decorativos */}
                <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-400 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute top-40 right-20 w-16 h-16 bg-pink-400 rounded-full opacity-20 animate-pulse delay-1000"></div>
                <div className="absolute bottom-40 left-20 w-24 h-24 bg-green-400 rounded-full opacity-20 animate-pulse delay-2000"></div>
                
                <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
                    <h1 className="hero-title text-5xl md:text-7xl font-bold mb-6 opacity-0 transform translate-y-8 transition-all duration-1000">
                        Laboratorio de Experimentos
                    </h1>
                    <p className="hero-subtitle text-xl md:text-2xl mb-8 opacity-0 transform translate-y-8 transition-all duration-1000 delay-300">
                        Descubre el mundo de la ciencia a través de experimentos divertidos y educativos
                    </p>
                    <div className="animate-fade-in-up opacity-0 transform translate-y-8 transition-all duration-1000 delay-500">
                        <p className="text-lg mb-8 max-w-2xl mx-auto">
                            Accede directamente a la documentación completa de los experimentos más importantes del Libro 1
                        </p>
                    </div>
                </div>
            </section>

            {/* Sección de Experimentos */}
            <section className="py-20 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 animate-on-scroll">
                            Experimentos Disponibles
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-on-scroll">
                            Cada experimento incluye instrucciones detalladas, materiales necesarios y objetivos de aprendizaje
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {experimentos.map((experimento, index) => (
                            <div 
                                key={experimento.id}
                                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 animate-on-scroll"
                                style={{ animationDelay: `${index * 200}ms` }}
                            >
                                <div className="relative h-48 bg-gradient-to-br from-purple-400 to-blue-500">
                                    <img 
                                        src={experimento.imagen} 
                                        alt={experimento.titulo}
                                        className="w-full h-full object-cover opacity-80"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                                        <span className="text-white text-2xl font-bold bg-purple-600 px-4 py-2 rounded-full">
                                            {experimento.titulo}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-800 mb-3">
                                        {experimento.titulo}
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        {experimento.descripcion}
                                    </p>
                                    
                                    <div className="mb-6">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Objetivos:</h4>
                                        <ul className="text-sm text-gray-600 space-y-1">
                                            {experimento.objetivos.map((objetivo, idx) => (
                                                <li key={idx} className="flex items-start">
                                                    <span className="text-green-500 mr-2">•</span>
                                                    {objetivo}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Sección de Tarjetas para el Experimento 4 */}
                                    {experimento.id === 4 && experimento.tarjetas && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Tarjetas del Experimento:</h4>
                                            <div className="space-y-2">
                                                <div className="flex space-x-2 mb-4">
                                                    {experimento.tarjetas.map((tarjeta, idx) => (
                                                        <a 
                                                            key={idx}
                                                            href={tarjeta}
                                                            download={`tarjeta_${idx + 1}_experimento4.png`}
                                                            className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-semibold py-2 px-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 text-center"
                                                        >
                                                            Tarjeta {idx + 1}
                                                        </a>
                                                    ))}
                                                </div>
                                                <button 
                                                    onClick={() => {
                                                        experimento.tarjetas.forEach((tarjeta, idx) => {
                                                            const link = document.createElement('a');
                                                            link.href = tarjeta;
                                                            link.download = `tarjeta_${idx + 1}_experimento4.png`;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                        });
                                                    }}
                                                    className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                                                >
                                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                    </svg>
                                                    Descargar Todas las Tarjetas
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Botón de Documentación solo para experimentos que no sean el 4 */}
                                    {experimento.id !== 4 && (
                                        <a 
                                            href={experimento.documento}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                                        >
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Ver Documentación
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Sección de Información Adicional */}
            <section className="py-20 bg-white">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold text-gray-800 mb-8 animate-on-scroll">
                        ¿Por qué estos experimentos?
                    </h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="animate-on-scroll">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Científicamente Probados</h3>
                            <p className="text-gray-600">Cada experimento ha sido diseñado siguiendo principios pedagógicos sólidos y está respaldado por investigación educativa.</p>
                        </div>
                        
                        <div className="animate-on-scroll">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Fácil de Seguir</h3>
                            <p className="text-gray-600">Instrucciones paso a paso con materiales comunes y explicaciones claras para padres y educadores.</p>
                        </div>
                        
                        <div className="animate-on-scroll">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">Diversión Garantizada</h3>
                            <p className="text-gray-600">Cada experimento está diseñado para ser emocionante y memorable, creando experiencias de aprendizaje positivas.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-800 text-white py-12">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h3 className="text-2xl font-bold mb-4">Princonser Kids</h3>
                    <p className="text-gray-300 mb-6">
                        Educando a través de la ciencia y la experimentación
                    </p>
                    <div className="flex justify-center space-x-6">
                        <a href="/" className="text-gray-300 hover:text-white transition-colors">
                            Inicio
                        </a>
                        <a href="/landingpage" className="text-gray-300 hover:text-white transition-colors">
                            Landing Page
                        </a>
                        <a href="/dashboard" className="text-gray-300 hover:text-white transition-colors">
                            Dashboard
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ExperimentosPage;
